import { Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement, ReactNode } from 'react';
import { ReceiptAlertType, getReceiptAlertTypeFilterTranslationId } from '@synergy/models';
import { findFilterValueTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useFilter } from '@synergy/components';
import { useSearchParams } from 'react-router-dom';

import { PALO_INCLUDE_LOCKED } from './palo-locked-records';
import { PALO_OPERATOR_LABEL, paloFilterParams } from './palo-filter-keys';
import { paloStatusNavLabel } from './palo-status-nav-label';

const c = tokens.color;

/**
 * One removable pill per filter the list is narrowed by.
 *
 * Read from the address rather than from the filter context: a filter now
 * carries its comparison — `filter[createdAt][from]` — and the context speaks
 * only in field names, so a chip removed through it would have rewritten
 * every other comparison into the plain form and quietly widened the list
 * (#2209).
 */
interface PaloFilterChipsProps {
    /**
     * What the list declared it can be filtered by.
     *
     * Only the kind of a field is read here, to tell an amount from a plain
     * number. A chip left without it still names and formats its filter; it
     * just cannot add the currency.
     */
    fields?: { key: string; type: string }[];
    /**
     * What an amount on this list is counted in, as the list already knows it.
     *
     * Left out, an amount is still written as one — with its thousands — but
     * without a unit, because a guessed currency is worse than none.
     */
    currency?: string;
    /**
     * What the values of a field are called, for the fields that hold a key
     * instead of a word.
     *
     * A chip used to learn this from the filter component beside it, which
     * only exists while the filter panel is open. Opened cold — a saved view,
     * a link someone passed on — nobody had said what the key stood for, and
     * the chip printed the key: "Sachbearbeiter 1.170" for Christian Marolt
     * (#2231 R-128). Handed in, the name holds without the panel.
     */
    names?: Record<string, { value: string; label: string }[]>;
}

export const PaloFilterChips = (props: PaloFilterChipsProps = {}): ReactElement | null => {
    const { getDisplayValue } = useFilter();
    const [searchParams, setSearchParams] = useSearchParams();
    const intl = useIntl();
    const kindOf = (key: string): string | undefined => props.fields?.find(field => field.key === key)?.type;

    const isNumber = (value: string): boolean => /^-?\d+([.,]\d+)?$/.test(value.trim());

    /**
     * An amount reads as an amount, with its thousands and its currency; any
     * other number, a document number above all, stays as it was typed.
     */
    const number = (value: string, kind?: string): string => {
        const asNumber = Number(value.trim().replace(',', '.'));

        if ('money' === kind) {
            return props.currency
                ? intl.formatNumber(asNumber, { style: 'currency', currency: props.currency })
                : intl.formatNumber(asNumber);
        }

        return intl.formatNumber(asNumber, { useGrouping: false, maximumFractionDigits: 10 });
    };

    const isDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

    const day = (value: string): string =>
        intl.formatDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' });

    // A document status is set by the sub-views of the navigation rather than by
    // a filter component, so nothing registers a label for it — and the chip
    // showed the constant where it should name the status.
    const one = (key: string, value: string): ReactNode => {
        // Same case as the status: the credit note sub-view sets this, so no
        // filter component registers a label and the chip would show "true".
        if ('isCredit' === key) {
            return (
                <FormattedMessage id={`app.invoices.detail.overview.isCredit.${'true' === value ? 'true' : 'false'}`} />
            );
        }

        // The kind of a notice travels as its constant; the chip read "Hinweis
        // DANGER". It is named as the filter dialog names it (#2340).
        if ('receiptAlertTypes' === key) {
            const kinds = Object.values(ReceiptAlertType) as string[];

            if (value === kinds.join(',')) {
                return <FormattedMessage id="app.filter.allReceiptAlertTypes" />;
            }

            if (kinds.includes(value)) {
                return <FormattedMessage id={getReceiptAlertTypeFilterTranslationId(value as ReceiptAlertType)} />;
            }
        }

        // A status the navigation offers is named the way the navigation names
        // it: clicking "Entwürfe" produced a chip reading "Status Erfasst"
        // (#2209, O-6).
        if ('status' === key) {
            const fromNavigation = paloStatusNavLabel(value);

            if (fromNavigation) {
                return fromNavigation;
            }

            // Where the navigation has no word, the list's own beats the
            // general one: an order is "Abgerechnet", not "Beendet mit
            // Folgebeleg" (#2264, #2340).
            const fromList = props.names?.['status']?.find(entry => entry.value === value)?.label;

            if (fromList) {
                return fromList;
            }
        }

        const valueId = findFilterValueTranslationId(value);

        if (valueId) {
            return <FormattedMessage id={valueId} />;
        }

        // What the list itself calls this value, where the list was asked.
        const named = props.names?.[key]?.find(entry => entry.value === value)?.label;

        if (named) {
            return named;
        }

        // A filter component that registered a wording for its values knows
        // more about them than this chip does — a contact is held as an id and
        // read as a name. Unregistered, the value comes back as it went in.
        const registered = getDisplayValue(key, value);

        if (registered !== value && null !== registered && undefined !== registered && '' !== registered) {
            return registered;
        }

        // A day travels in the address the way the server reads it; on a chip
        // it is read by a person.
        if (isDay(value)) {
            return day(value);
        }

        // Likewise a number: 1000 is written the way an amount is written, and
        // an amount says what it is counted in.
        if (isNumber(value)) {
            return number(value, kindOf(key));
        }

        // Nothing named it: a country code is still worth more than a chip
        // that names the field and leaves the value out ("Land" for DE).
        return registered || value;
    };

    /**
     * What one filter says, values and all.
     *
     * A comparison that reads several values carries them separated by a
     * comma, which on a chip reads as a decimal comma instead: "zwischen
     * 1000,5000" is one amount, not two (#2231 R-127). A range is written as
     * a range, a list as a list.
     */
    const displayOf = (key: string, value: string, operator?: string): ReactNode => {
        const parts = value.split(',');

        if ('between' === operator && 2 === parts.length) {
            return (
                <>
                    {one(key, parts[0])} – {one(key, parts[1])}
                </>
            );
        }

        if ('in' === operator && 1 < parts.length) {
            return parts.map((part, index) => (
                <span key={part}>
                    {0 < index ? ', ' : ''}
                    {one(key, part)}
                </span>
            ));
        }

        return one(key, value);
    };

    // The two comparisons a field implies go unsaid, the way nobody says
    // "Status ist Erfasst" out loud; the others are named before the value.
    const said = (operator?: string): string =>
        !operator || 'eq' === operator || 'contains' === operator
            ? ''
            : `${PALO_OPERATOR_LABEL[operator] ?? operator} `;

    /**
     * The wording written for a chip, if there is one.
     *
     * `app.filter.…` holds two different things: wordings that read a value
     * ("Sachbearbeiter {value}") and the plain labels the filter panel puts
     * over its fields ("Sachbearbeiter", "Kunde", "Lieferant"). Handed the
     * second as a chip, the message formatter has nowhere to put the value
     * and drops it, so the chip named the field and swallowed what it was
     * narrowed to (#2231 R-122). Only a wording with a place for the value
     * counts as one.
     */
    const wording = (id: string): boolean => {
        const held = intl.messages[id];

        return 'string' === typeof held && held.includes('{value');
    };

    // A field the list has only just declared may have no wording of its own
    // yet, and then the chip falls back to the field's label and finally to
    // its name — never to an untranslated message id.
    const name = (key: string): string =>
        intl.messages[`app.label.${key}`] ? intl.formatMessage({ id: `app.label.${key}` }) : key;

    // "is empty" and "is filled" carry a `1` in the address to say they are
    // set at all. That is a switch, not a value, and printing it read "ist
    // leer 1 Stern" (#2231 R-121).
    const standsAlone = (operator?: string): boolean => 'empty' === operator || 'notEmpty' === operator;

    // A switch in the filter row is a filter too, and it stands as a chip
    // like the others — but it carries no value to print: "inkl. gesperrte 1"
    // is not something anyone writes (LST-S14).
    // The inquiries' "Seit 21 Tagen offen" is a switch of the same kind (#2260).
    const switched: Record<string, string> = {
        [PALO_INCLUDE_LOCKED]: 'inkl. gesperrte',
        longOpen: 'Seit 21 Tagen offen',
        // The cockpit lists open documents unless asked for all (#2326 R-235).
        scope: 'inkl. beendete',
    };

    const chips = paloFilterParams(searchParams).map(entry => ({
        ...entry,
        id: switched[entry.key] ? '' : `app.filter.${entry.key}`,
        name: switched[entry.key] ?? name(entry.key),
        said: said(entry.operator),
        display:
            switched[entry.key] || standsAlone(entry.operator)
                ? null
                : displayOf(entry.key, entry.value, entry.operator),
        // A wording that counts — "1 Stern" against "5 Sterne" — needs the
        // number itself to choose by. It used to be handed the finished chip
        // text instead, so the choice never fell on the singular and every
        // rating read "1 Sterne" (#2231 R-127).
        // Anything that is not one number — a range, a list, a name — falls to
        // the plural's own "other" wording, which is the one written for it.
        count: isNumber(entry.value) ? Number(entry.value.replace(',', '.')) : -1,
    }));

    // The search term narrows the list as a filter does, and it is kept as
    // long as one — a list opened on another day came back searched, said so
    // nowhere, and read as empty (#2221, A11). It is not a filter in the
    // address, though: the field in the bar owns it. So it is put in front of
    // the chips here, and removing it empties that field, which reads from
    // the same place.
    const searched = searchParams.get('search');

    if (searched) {
        chips.unshift({
            param: 'search',
            value: searched,
            key: 'search',
            operator: undefined,
            id: '',
            name: intl.messages['app.common.search'] ? intl.formatMessage({ id: 'app.common.search' }) : 'Suche',
            said: '',
            display: searched,
            count: -1,
        });
    }

    if (chips.length === 0) {
        return null;
    }

    const removeChip = (removeParam: string, removeValue: string): void => {
        const next = new URLSearchParams();

        Array.from(searchParams.entries())
            .filter(([param, value]) => !(param === removeParam && value === removeValue))
            .forEach(([param, value]) => next.append(param, value));

        next.set('page', '1');
        setSearchParams(next);
    };

    /** Every chip at once; page size, sorting and columns are not filters and stay (R-271). */
    const removeAll = (): void => {
        const next = new URLSearchParams();

        Array.from(searchParams.entries())
            .filter(([param, value]) => !chips.some(chip => chip.param === param && chip.value === value))
            .forEach(([param, value]) => next.append(param, value));

        next.set('page', '1');
        setSearchParams(next);
    };

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: 1.5 }}>
            {chips.map(chip => (
                <Box
                    key={chip.param + chip.value}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: c.accentSoft,
                        color: c.accentHi,
                        borderRadius: '12px',
                        padding: '3px 6px 3px 10px',
                        fontSize: '12px',
                        fontWeight: 500,
                    }}
                >
                    <Box component="span">
                        {/* A comparison that stands alone is said with the
                            field's own name: the wording under `app.filter.…`
                            is built around a value this one does not have. */}
                        {wording(chip.id) && null !== chip.display ? (
                            <FormattedMessage
                                id={chip.id}
                                values={{
                                    count: chip.count,
                                    value: (
                                        <>
                                            {chip.said}
                                            <b>{chip.display}</b>
                                        </>
                                    ),
                                }}
                            />
                        ) : null === chip.display ? (
                            <>
                                {chip.name} <b>{chip.said.trim()}</b>
                            </>
                        ) : (
                            <>
                                {chip.name} {chip.said}
                                <b>{chip.display}</b>
                            </>
                        )}
                    </Box>
                    <Box
                        role="button"
                        aria-label="Filter entfernen"
                        onClick={() => removeChip(chip.param, chip.value)}
                        sx={{
                            display: 'flex',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            color: c.accentHi,
                            '&:hover': { backgroundColor: 'rgba(15,79,173,.14)' },
                            '& svg': { fontSize: 15 },
                        }}
                    >
                        <Close />
                    </Box>
                </Box>
            ))}
            <Box
                component="button"
                type="button"
                onClick={removeAll}
                sx={{
                    border: 0,
                    background: 'none',
                    padding: '3px 4px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '12px',
                    color: c.accentHi,
                    textDecoration: 'underline',
                }}
            >
                Filter zurücksetzen
            </Box>
        </Box>
    );
};
